<template>
    <div class="import-feedback-drawer-import-step">
        <pendo-card class="import-feedback-drawer-import-step__card">
            <div class="import-feedback-drawer-import-step__content">
                <img
                    src="@img/voc/loading-csv-items.svg"
                    width="70"
                    height="70">
                <h3>{{ subText }}</h3>
                <p class="import-feedback-drawer-import-step__subtitle">
                    This may take a few minutes, you can close this window and we'll let you know when its all imported.
                </p>
                <pendo-progress-bar :value="progress" />
                <span class="import-feedback-drawer-import-step__content-loading-items">
                    {{ processedItemsText }}
                </span>
                <div v-if="!isFileOrUnknownSource">
                    <import-feedback-drawer-guide-bar-card
                        :guide="guide"
                        :type="guideType" />
                </div>
                <div v-if="isFile">
                    <import-feedback-drawer-csv-bar-card :file-name="fileName" />
                </div>
                <div class="import-feedback-drawer-import-step__carousel-wrapper">
                    <tip-carousel :tips="tips" />
                </div>
                <pendo-divider width="60%" />
            </div>
        </pendo-card>
    </div>
</template>

<script>
import { PendoCard, PendoProgressBar, PendoDivider } from '@pendo/components';
import { pluralize } from '@/utils/formatters';
import { mapState } from 'vuex';
import TipCarousel from '@/voc/components/tip-carousel/tip-carousel.vue';
import ImportFeedbackDrawerGuideBarCard from '@/voc/components/drawers/import-feedback-drawer/components/import-feedback-drawer-guide-bar-card/import-feedback-drawer-guide-bar-card.vue';
import ImportFeedbackDrawerCsvBarCard from '@/voc/components/drawers/import-feedback-drawer/components/import-feedback-drawer-csv-bar-card.vue';

export default {
    components: {
        PendoCard,
        PendoProgressBar,
        PendoDivider,
        TipCarousel,
        ImportFeedbackDrawerGuideBarCard,
        ImportFeedbackDrawerCsvBarCard,
    },
    props: {
        guide: {
            type: [Object, null],
            required: false,
            default: null,
        },
        fileName: {
            type: String,
            required: false,
            default: '',
        },
        importStatus: {
            type: String,
            required: true,
        },
        processedItems: {
            type: Number,
            required: true,
        },
        totalItems: {
            type: Number,
            required: true,
        },
        type: {
            type: String,
            default: '',
        },
    },
    computed: {
        ...mapState({
            tips: (state) => state.voc.onboarding.importTips,
        }),
        progress () {
            if (this.importStatus === 'not started' || this.importStatus === 'preparing' || this.totalItems === 0) {
                return 0;
            }

            return Math.round((this.processedItems / this.totalItems) * 100) || 0;
        },
        processedItemsText () {
            if (this.importStatus === 'not started' || this.importStatus === 'preparing') {
                return 'Import starting...';
            }

            return `${this.processedItems} of ${this.totalItems} ${pluralize('item', this.totalItems)} processed`;
        },
        subText () {
            if (this.isFileOrUnknownSource) {
                return 'Importing feedback';
            }

            return 'Sending historic responses to Listen';
        },
        isFileOrUnknownSource () {
            return this.type === 'file' || this.type === 'unknown';
        },
        isFile () {
            return this.type === 'file';
        },
        guideType () {
            return this.type === 'guide' ? 'Guide' : 'NPS';
        },
    },
};
</script>

<style lang="less" scoped>
.import-feedback-drawer-import-step {
    .pendo-card {
        :deep(.pendo-card__body) {
            padding: 40px 24px;
        }
    }

    &__card {
        display: flex;
        flex-direction: column;
        width: 700px;
        margin: auto;
    }

    &__content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;

        & h3 {
            font-weight: 600;
            margin: 16px 0px 6px 0px;
        }

        &-loading-subtitle {
            color: var(--palette-gray-90);
            margin: 0px;
        }

        &-loading-items {
            color: var(--palette-gray-60);
            margin: 2px 0px 0px 0px;
        }

        :deep(.pendo-progress-bar) {
            margin: 8px 0px;
        }
    }
    &__subtitle {
        max-width: 450px;
        text-align: center;
        margin: 16px 0;
        line-height: 150%;
    }
    &__carousel-wrapper {
        width: 600px;
    }
}
</style>
